import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import TopModalSection from '../common/TopModalSection';
import LoadingSkeletonHaggleForm from './LoadingSkeletonHaggleForm';

import HaggleEventForm from '@/components/forms/haggle-event/HaggleEventForm';
import { HaggleType } from '@/components/utils/enums';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { HaggleEventFormOptions } from '@/interfaces/api/haggle-event/HaggleEventFormOptions.enum';
import { ICreateHaggleEventWithTimeOnForm } from '@/interfaces/api/haggle-event/ICreateHaggleEventPayload';
import { IHaggleEvent } from '@/interfaces/api/haggle-event/IHaggleEvent';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

type Props = {
	defaultClose: () => void;
	id?: number | undefined;
	closeAfterSubmit?: () => void;
	haggleOption?: HaggleEventFormOptions;
	refetchHaggleEvents?: () => void;
	isBusinessEvent?: boolean;
};

const HaggleEventFormModal = ({
	defaultClose,
	id,
	closeAfterSubmit,
	haggleOption = HaggleEventFormOptions.CREATE,
	refetchHaggleEvents,
	isBusinessEvent,
}: Props) => {
	const [isLoading, setIsLoading] = useState(false);

	const { user } = useUser();

	const {
		data: products,
		isLoading: isFetching,
		isError,
	} = useQuery<ICollectionProduct>({
		queryKey: [ApiQueryKeys.getAllStoreProducts],
		queryFn: () => haggleApiService.getAllStoreProducts(),
		refetchOnWindowFocus: false,
	});

	const {
		data: updateHaggleEvent,
		isLoading: isUpdateLoading,
		refetch,
	} = useQuery<IHaggleEvent>({
		queryKey: [ApiQueryKeys.getHaggleEventById, id],
		queryFn: () => haggleApiService.getHaggleEventById(Number(id)),
		enabled:
			id !== undefined &&
			typeof id === 'number' &&
			haggleOption !== HaggleEventFormOptions.CREATE,
		refetchOnWindowFocus: true,
	});

	const handleCreateSubmit = async (
		values: ICreateHaggleEventWithTimeOnForm,
	) => {
		setIsLoading(true);

		try {
			const { startDateTime, endDateTime, password, invitedEmails, ...rest } =
				values;

			const parsedValues = {
				...rest,
				showHaggleBannerOnShopify: values.showHaggleBannerOnShopify === 'true',
				addAutoShopifyPolicy: values.addAutoShopifyPolicy === 'true',
				freeShippingThreshold: values.freeShippingThreshold || null,
				customReturnAndExchangePolicy:
					values.customReturnAndExchangePolicy || null,
				shippingPolicy: values.shippingPolicy || null,
				upcomingPreviewDate: values.upcomingPreviewDate
					? new Date(
							new Date(values.upcomingPreviewDate).toUTCString(),
					  ).toISOString()
					: null,
				followUpOfferValidity: values.followUpOfferValidity
					? new Date(
							new Date(values.followUpOfferValidity).toUTCString(),
					  ).toISOString()
					: null,
				startDate: new Date(
					new Date(values.startDateTime).toUTCString(),
				).toISOString(),
				endDate: new Date(
					new Date(values.endDateTime).toUTCString(),
				).toISOString(),
				hostHaggleEventOnShopify:
					user?.inventoryType === InventoryType.CSV
						? false
						: values.hostHaggleEventOnShopify === 'true',
				...(values.haggleType ? { haggleType: values.haggleType } : null),

				...(values.haggleType &&
				[HaggleType.PRIVATE, HaggleType.SEMI_PRIVATE].includes(
					values.haggleType,
				) &&
				password
					? { password }
					: null),
				isCSVHaggle: user?.inventoryType === InventoryType.CSV,
				invitedEmails: invitedEmails || [],
				minOrderValue: values.minOrderValue || null,
				minOrderQuantity: values.minOrderQuantity || null,
			};

			const createdEventResponse = await haggleApiService.createHaggleEvent(
				parsedValues,
			);
			notificationService.success('Haggle Event created successfully');

			const { startDate, endDate, id: haggleEventId } = createdEventResponse;

			if (user?.username && startDate && endDate) {
				await haggleApiService.markEventAsFavourite({
					startDate,
					endDate,
					username: user.username,
				});
			}

			if (
				values.haggleType === HaggleType.PRIVATE &&
				Array.isArray(invitedEmails) &&
				invitedEmails.length > 0
			) {
				await haggleApiService.createHaggleInvite({
					merchantId: user?.id,
					haggleEventId,
					invitedEmails,
					haggleType: values.haggleType,
					password: password,
				});
			}

			defaultClose();
			await refetchHaggleEvents?.();
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to create a Haggle Event: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
		}
	};
	const handleUpdateSubmit = async (
		values: ICreateHaggleEventWithTimeOnForm,
	) => {
		if (!id) return;

		setIsLoading(true);
		try {
			const { startDateTime, endDateTime, invitedEmails, password, ...rest } =
				values;
			const parsedValues = {
				...rest,
				showHaggleBannerOnShopify: values.showHaggleBannerOnShopify === 'true',
				addAutoShopifyPolicy: values.addAutoShopifyPolicy === 'true',
				freeShippingThreshold: values.freeShippingThreshold || null,
				customReturnAndExchangePolicy:
					values.customReturnAndExchangePolicy || null,
				shippingPolicy: values.shippingPolicy || null,
				upcomingPreviewDate: values.upcomingPreviewDate
					? new Date(
							new Date(values.upcomingPreviewDate).toUTCString(),
					  ).toISOString()
					: null,
				followUpOfferValidity: values.followUpOfferValidity
					? new Date(
							new Date(values.followUpOfferValidity).toUTCString(),
					  ).toISOString()
					: null,
				startDate: new Date(
					new Date(values.startDateTime).toUTCString(),
				).toISOString(),
				endDate: new Date(
					new Date(values.endDateTime).toUTCString(),
				).toISOString(),
				hostHaggleEventOnShopify:
					user?.inventoryType === InventoryType.CSV
						? false
						: values.hostHaggleEventOnShopify === 'true',
				isCSVHaggle: user?.inventoryType === InventoryType.CSV,
				haggleType: values.haggleType,
				invitedEmails: values.invitedEmails || [],
				password: password || null,
				minOrderValue: values.minOrderValue || null,
				minOrderQuantity: values.minOrderQuantity || null,
			};

			await haggleApiService.updateHaggleEvent(Number(id), parsedValues);

			notificationService.success('Haggle Event updated successfully');

			if (
				values.haggleType === HaggleType.PRIVATE &&
				Array.isArray(invitedEmails) &&
				invitedEmails.length > 0
			) {
				await haggleApiService.createHaggleInvite({
					merchantId: user?.id,
					haggleEventId: Number(id),
					invitedEmails,
					haggleType: values.haggleType,
					password: password,
				});
			}

			refetch();
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to update a Haggle Event: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
			closeAfterSubmit?.();
		}
	};

	const handleInitialValues = (
		haggleEvent: IHaggleEvent | undefined,
		allProducts: IProduct[],
	): ICreateHaggleEventWithTimeOnForm | undefined => {
		if (haggleEvent) {
			const completeProducts =
				haggleEvent.minimalProducts && haggleEvent.minimalProducts.length > 0
					? haggleEvent.minimalProducts.map((minimalProduct) => {
							const productId =
								typeof minimalProduct.id === 'string'
									? parseInt(minimalProduct.id, 10)
									: minimalProduct.id;

							const productObject = {
								id: productId,
								title: minimalProduct.title,
								handle: minimalProduct.handle,
								// Add minimal required fields for the form to work
								body_html: '',
								product_info: null,
								image: {} as any,
								images: [],
								options: [],
								product_type: minimalProduct.productType,
								published_at: '',
								published_scope: '',
								tags: '',
								template_suffix: null,
								variants:
									minimalProduct.variants?.map((variant) => ({
										id: Number(variant.id),
										product_id: productId,
										title: variant.title,
										price: variant.price,
										compare_at_price: variant.compare_at_price || null,
										inventory_quantity: variant.inventory_quantity,
										position: 1,
										option1: null,
										option2: null,
										option3: null,
										barcode: '',
										created_at: '',
										updated_at: '',
										fulfillment_service: '',
										grams: 0,
										image_id: null,
										inventory_item_id: 0,
										inventory_management: '',
										old_inventory_quantity: variant.inventory_quantity,
										requires_shipping: true,
										sku: '',
										taxable: true,
										tax_code: null,
										weight: 0,
									})) || [],
								vendor: '',
								tradeName: '',
								status: 'active' as const,
								created_at: '',
								updated_at: '',
								uuid: '',
								createdAt: '',
								updatedAt: '',
								deletedAt: '',
							} as IProduct;

							return productObject;
					  })
					: allProducts.filter((product) =>
							haggleEvent.productIds.includes(product.id.toString()),
					  );

			return {
				startDateTime: haggleEvent.startDate,
				endDateTime: haggleEvent.endDate,
				acceptableMargin: haggleEvent.acceptableMargin,
				productIds: completeProducts,
				limitCheckoutTime: haggleEvent.limitCheckoutTime,
				showHaggleBannerOnShopify:
					haggleEvent.showHaggleBannerOnShopify?.toString() ?? null,
				freeShippingThreshold: haggleEvent.freeShippingThreshold ?? null,
				addAutoShopifyPolicy:
					haggleEvent.addAutoShopifyPolicy?.toString() ?? null,
				customReturnAndExchangePolicy:
					haggleEvent.customReturnAndExchangePolicy ?? null,
				shippingPolicy: haggleEvent.shippingPolicy ?? null,
				upcomingPreviewDate: haggleEvent.upcomingPreviewDate || null,
				followUpOfferValidity: haggleEvent.followUpOfferValidity || null,
				hostHaggleEventOnShopify:
					user?.inventoryType === InventoryType.CSV
						? 'false'
						: haggleEvent.hostHaggleEventOnShopify?.toString() ?? null,
				isCSVHaggle: user?.inventoryType === InventoryType.CSV,
				haggleType: haggleEvent.haggleType,
				invitedEmails: haggleEvent.invitedEmails || [],
				password: haggleEvent.password || null,
				minOrderValue: haggleEvent.minOrderValue || null,
				minOrderQuantity: haggleEvent.minOrderQuantity || null,
			};
		}

		return undefined;
	};

	if (isError) {
		defaultClose();
	}

	const loading = isFetching || isUpdateLoading;

	const isUpdateMode =
		haggleOption === HaggleEventFormOptions.UPDATE ||
		haggleOption === HaggleEventFormOptions.VIEW;
	const shouldShowForm =
		products && !loading && (!isUpdateMode || updateHaggleEvent);

	return (
		<div className="flex flex-col justify-center items-center w-full">
			<TopModalSection
				className="gap-x-4 border-b py-2 w-full px-4"
				title={
					haggleOption === HaggleEventFormOptions.UPDATE
						? 'Update Haggle Event'
						: haggleOption === HaggleEventFormOptions.CREATE
						? 'Create new Haggle Event'
						: 'View Haggle Event'
				}
				close={defaultClose}
			/>

			{!shouldShowForm && <LoadingSkeletonHaggleForm />}

			<div className="relative z-0">
				{shouldShowForm && (
					<HaggleEventForm
						handleSubmit={
							updateHaggleEvent &&
							haggleOption === HaggleEventFormOptions.UPDATE
								? handleUpdateSubmit
								: handleCreateSubmit
						}
						products={products.data as IProduct[]}
						loading={loading || isLoading}
						initialValues={handleInitialValues(
							updateHaggleEvent,
							products.data as IProduct[],
						)}
						haggleOption={
							updateHaggleEvent &&
							haggleOption === HaggleEventFormOptions.UPDATE
								? HaggleEventFormOptions.UPDATE
								: haggleOption === HaggleEventFormOptions.CREATE
								? HaggleEventFormOptions.CREATE
								: HaggleEventFormOptions.VIEW
						}
						isBusinessEvent={isBusinessEvent}
						inventoryType={user?.inventoryType || InventoryType.SHOPIFY}
					/>
				)}

				{/* Modal must be rendered at root level, not affected by parent containers */}
			</div>
		</div>
	);
};

export default HaggleEventFormModal;
